import { getCenterFromElement } from "../utils/getCenterFromElement";
import { getDirectionFromTiles } from "../utils/getDirectionFromTiles";
import { getWordFromSelection } from "../utils/getWordFromSelection";
import { selectorCheck } from "../utils/selectorCheck";
import { wait } from "../utils/wait";
import { findClosestTile, findClosestTileBruteForce } from "../utils/findClosestTile";
import { fetchByLocalPos } from "../utils/fetchByPos";
import { SOPA_SOUNDS } from "../utils/consts";
import { useGameSounds } from "@educaplay/core";
import { useRef } from "react";

/**
 * Hook para manejar la lógica de selección de palabras
 */
const DELAY_SOUND = 250;

export const useWordSelection = ({
  currentQuestion,
  currentQuestionIndex,
  questions,
  words,
  tiles,
  gridSize,
  tileRefs,
  hasFinished,
  penalties,
  userAnswers,
  setUserAnswers,
  foundWordsArray,
  setFoundWordsArray,
  setActiveSelector,
  setGridShown,
  onAnswer,
  gridRef
}) => {

  const gameSound = useGameSounds();
  const isProcessingRef = useRef(false);

  const chainToSelector = (tileChain) => {
    const origin = tileChain[0];
    const destination = tileChain[tileChain.length - 1];

    return {
      origin,
      destination,
      center: getCenterFromElement(tileRefs.current[origin.id]),
      direction: getDirectionFromTiles(origin, destination),
      state: 0,
    };
  };

  const pointerUpHandler = async (keyboardData) => {
    if (hasFinished || isProcessingRef.current) return;
    let selectorLocal = null;
    if (keyboardData) {
      selectorLocal = chainToSelector(keyboardData);
    } else {
      return;
    }

    if (!selectorLocal) {
      return;
    }

    if (selectorLocal.origin === selectorLocal.destination) return;

    const existing = userAnswers.find(
      (it) =>
        (it.origin === selectorLocal.origin &&
          it.destination === selectorLocal.destination) ||
        (it.origin === selectorLocal.destination &&
          it.destination === selectorLocal.origin)
    );

    if (existing?.state === 1) {
      return;
    }

    const target = getCenterFromElement(
      tileRefs.current[selectorLocal.destination.id]
    );

    selectorLocal.target = target;
    selectorLocal.word = getWordFromSelection(selectorLocal, tiles, gridSize);

    if (!selectorLocal.word) return;

    if (selectorLocal.enunId === undefined) {
      selectorLocal.enunId = selectorCheck(words, selectorLocal);
    }

    selectorLocal.state = selectorLocal.enunId !== -1 ? 1 : penalties ? 2 : 3;

    if (selectorLocal.enunId !== -1) {
      gameSound.play(SOPA_SOUNDS.CORRECT_WORD);
      selectorLocal.shouldAnimate = true;
    } else if (penalties) {
      isProcessingRef.current = true;
      gameSound.play(SOPA_SOUNDS.LETRA_ERROR);
    } else {
      setTimeout(() => {
              gameSound.play(SOPA_SOUNDS.MOVER_LETRA);
      }, DELAY_SOUND);
    }

    let newAnswer;
    if (selectorLocal.enunId !== -1) {
      newAnswer = userAnswers.concat(selectorLocal);
    } else {
      const tempId = Date.now() + Math.random();
      const selectorWithId = { ...selectorLocal, tempId };
      newAnswer = userAnswers.concat(selectorWithId);
      
      const delay = penalties ? 1000 : 500;
      setTimeout(() => {
        setUserAnswers(prevAnswers => 
          prevAnswers.filter(answer => answer.tempId !== tempId)
        );
        if (penalties) {
          isProcessingRef.current = false;
        }
      }, delay);
    }

    const allFound =
      newAnswer.filter((it) => it.enunId !== -1).length >=
      currentQuestion.correctAnswers.length;

    if (selectorLocal.enunId !== -1) {
      const foundCopy = foundWordsArray.map((it) => it);
      foundCopy[selectorLocal.enunId] = true;
      setFoundWordsArray(foundCopy);
    }
    setUserAnswers(newAnswer);

    onAnswer(newAnswer, {
      showFeedback: penalties,
      playErrorSound: penalties,
      nextQuestionIndex: allFound ? undefined : currentQuestionIndex,
    });

    if (!allFound) return;

    if (currentQuestionIndex + 1 < questions.length) {
      await wait(200);
      setGridShown(false);
      await wait(200);
      setUserAnswers([]);
    }
  };

  const handleActivePointerUp = async (activeSelector) => {
    // Allow selection when time is 0 (no time limit) but prevent when game has finished
    if (hasFinished) {
      return;
    }
    
    if (!activeSelector) {
      return;
    }
    
    const finalSelector = { ...activeSelector };

    if (!finalSelector.destination || finalSelector.destination === finalSelector.origin) {
      const gridRect = gridRef?.current?.getBoundingClientRect();
      if (gridRect && finalSelector.target) {
        const destinationTileId = fetchByLocalPos(
          finalSelector.target.x,
          finalSelector.target.y,
          gridRect,
          gridSize
        );

        if (destinationTileId !== null) {
          finalSelector.destination = tiles[destinationTileId];
        } else {
          const closestTile = findClosestTile(
            finalSelector.target.x,
            finalSelector.target.y,
            gridRect,
            gridSize,
            tiles
          );

          if (closestTile) {
            finalSelector.destination = closestTile;
            finalSelector.target = getCenterFromElement(tileRefs.current[closestTile.id]);
          } else {
            const bruteForceTile = findClosestTileBruteForce(
              finalSelector.target.x,
              finalSelector.target.y,
              gridRect,
              gridSize,
              tiles,
              tileRefs
            );

            if (bruteForceTile) {
              finalSelector.destination = bruteForceTile;
              finalSelector.target = getCenterFromElement(tileRefs.current[bruteForceTile.id]);
            }
          }
        }
      }
    }

    setActiveSelector(null);

    if (isProcessingRef.current) return;

    if (finalSelector.origin === finalSelector.destination) return;

    const existing = userAnswers.find(
      (it) =>
        (it.origin === finalSelector.origin &&
          it.destination === finalSelector.destination) ||
        (it.origin === finalSelector.destination &&
          it.destination === finalSelector.origin)
    );

    if (existing?.state === 1) {
      return;
    }

    if (finalSelector.destination) {
      finalSelector.target = getCenterFromElement(
        tileRefs.current[finalSelector.destination.id]
      );
    }

    // Calculate direction if not already set
    if (!finalSelector.direction && finalSelector.destination) {
      finalSelector.direction = getDirectionFromTiles(finalSelector.origin, finalSelector.destination);
    }

    finalSelector.word = getWordFromSelection(finalSelector, tiles, gridSize);

    if (!finalSelector.word) return;


    const expectedLength = Math.abs(finalSelector.destination.position.x - finalSelector.origin.position.x) +
      Math.abs(finalSelector.destination.position.y - finalSelector.origin.position.y) + 1;
    const actualLength = finalSelector.word.length;

    if (actualLength < expectedLength) {
      const realDestinationX = finalSelector.origin.position.x + (finalSelector.direction.x * (actualLength - 1));
      const realDestinationY = finalSelector.origin.position.y + (finalSelector.direction.y * (actualLength - 1));
      const realDestinationId = realDestinationY * gridSize + realDestinationX;

      if (realDestinationId >= 0 && realDestinationId < tiles.length) {
        const realDestination = tiles[realDestinationId];
        finalSelector.destination = realDestination;
        finalSelector.target = getCenterFromElement(tileRefs.current[realDestination.id]);
      }
    }

    if (finalSelector.enunId === undefined) {
      finalSelector.enunId = selectorCheck(words, finalSelector);
    }

    finalSelector.state = finalSelector.enunId !== -1 ? 1 : penalties ? 2 : 3;

    if (finalSelector.enunId !== -1) {
      gameSound.play(SOPA_SOUNDS.CORRECT_WORD);
      finalSelector.shouldAnimate = true;
    } else if (penalties) {
      isProcessingRef.current = true;
      gameSound.play(SOPA_SOUNDS.LETRA_ERROR);
    } else {
      setTimeout(() => {
              gameSound.play(SOPA_SOUNDS.MOVER_LETRA);
      }, DELAY_SOUND);
    }

    let newAnswer;
    if (finalSelector.enunId !== -1) {
      newAnswer = userAnswers.concat(finalSelector);
    } else {
      const tempId = Date.now() + Math.random();
      const selectorWithId = { ...finalSelector, tempId };
      newAnswer = userAnswers.concat(selectorWithId);
      
      const delay = penalties ? 1000 : 500;
      setTimeout(() => {
        setUserAnswers(prevAnswers => 
          prevAnswers.filter(answer => answer.tempId !== tempId)
        );
        if (penalties) {
          isProcessingRef.current = false;
        }
      }, delay);
    }

    const allFound =
      newAnswer.filter((it) => it.enunId !== -1).length >=
      currentQuestion.correctAnswers.length;

    if (finalSelector.enunId !== -1) {
      const foundCopy = foundWordsArray.map((it) => it);
      foundCopy[finalSelector.enunId] = true;
      setFoundWordsArray(foundCopy);
    }
    setUserAnswers(newAnswer);

    onAnswer(newAnswer, {
      showFeedback: penalties,
      playErrorSound: false,
      nextQuestionIndex: allFound ? undefined : currentQuestionIndex,
    });

    if (!allFound) return;

    if (currentQuestionIndex + 1 < questions.length) {
      await wait(200);
      setGridShown(false);
      await wait(200);
      setUserAnswers([]);
    }
  };

  return {
    chainToSelector,
    pointerUpHandler,
    handleActivePointerUp
  };
};
